import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAccess } from "utils/hooks/access";
import { useCompanyCurrency } from "utils/hooks/company";
import { currencyFormatter } from "utils/helpers/currencyFormatter";
import { renderDurationAsFormat } from "utils/helpers/date";
import { cn } from "lib/utils";
import type { TimesheetType } from "../../../../types";

const hourTypes = [
    "regular",
    "overtime",
    "double-time",
    "pto",
    "amount",
    "unit",
    "holiday",
];

function Widget({ 
    type, 
    isLoading, 
    overviewResponse
 }: { type: TimesheetType, overviewResponse: any, isLoading: boolean }) {
    const { t } = useTranslation();
    const isMyTimesheet = type === "my-timesheet";
    const { hasAccess: canViewRates } = useAccess(isMyTimesheet ? "canViewMyRates" : "canViewRates");
    const currency = useCompanyCurrency();

    const { rows, totalDuration, totalPayment, budget, hasBudget } = useMemo(() => {
        const items: any[] = !overviewResponse
            ? []
            : Array.isArray(overviewResponse)
                ? overviewResponse
                : (overviewResponse as any).data || [];

        const budgetFromResponse = !overviewResponse || Array.isArray(overviewResponse)
            ? null
            : (overviewResponse as any).budget ?? null;
        const budget = budgetFromResponse ?? { hours: 0, amount: 0 };
        const hasBudget = budgetFromResponse !== null;

        const byHourType = Object.fromEntries(
            items.map((item: any) => [item.hourType, item])
        );

        let totalDuration = 0;
        let totalPayment = 0;

        const rows = hourTypes.map((hourType) => {
            const item = byHourType[hourType];
            const duration = item?.duration || 0;
            const amount = item?.total || 0;
            totalDuration += duration;
            totalPayment += amount;
            return { hourType, duration, amount };
        });

        return { rows, totalDuration, totalPayment, budget, hasBudget };
    }, [overviewResponse]);

    const durationVar = useMemo(() => {
        const diff = budget.hours - totalDuration;
        return { value: Math.abs(diff), isNegative: diff < 0 };
    }, [budget.hours, totalDuration]);

    const amountVar = useMemo(() => {
        const diff = budget.amount - totalPayment;
        return { value: Math.abs(diff), isNegative: diff < 0 };
    }, [budget.amount, totalPayment]);


    if (isLoading) {
        return (
            <div className="tw-flex tw-flex-col tw-gap-1.5 tw-py-3 tw-px-2 tw-bg-transparent tw-rounded tw-shrink-0 tw-w-72 tw-overflow-y-auto tw-h-full">
                {hourTypes.map((hourType) => (
                    <div key={hourType} className="tw-flex tw-flex-col tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-bg-white tw-border-border tw-animate-pulse">
                        <div className="tw-h-2.5 tw-w-8 tw-bg-muted tw-rounded tw-mb-2" />
                        <div className="tw-h-4 tw-w-14 tw-bg-muted tw-rounded" />
                    </div>
                ))}
                <div className="tw-border-t tw-border-border tw-mx-1 tw-my-0.5" />
                <div className="tw-flex tw-flex-col tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-bg-foreground/20 tw-animate-pulse">
                    <div className="tw-h-2.5 tw-w-10 tw-bg-muted tw-rounded tw-mb-2" />
                    <div className="tw-h-4 tw-w-14 tw-bg-muted tw-rounded" />
                </div>
            </div>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-1.5 tw-px-2 tw-bg-white tw-rounded tw-shrink-0 tw-w-72 tw-overflow-y-auto tw-h-full">
            {rows.map(({ hourType, duration, amount }) => {
                const hasValue = duration > 0;
                return (
                    <div
                        key={hourType}
                        className={cn("tw-flex tw-flex-col tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-transition-colors tw-text-xs",
                            hasValue ? "tw-bg-primary/5 tw-border-primary/20" : "tw-bg-white tw-border-border")}
                    >
                        <span
                            className={cn("tw-uppercase tw-font-bold tw-tracking-wider",
                                hasValue ? "tw-text-primary" : "tw-text-muted-foreground")}
                        >
                            {t(hourType)}
                        </span>
                        <span
                            className={cn("tw-font-semibold tw-mt-1",
                                hasValue ? "tw-text-foreground" : "tw-text-muted-foreground"
                            )}
                        >
                            {hasValue ? renderDurationAsFormat(duration, "HH:mm") : "--"}
                        </span>
                        {canViewRates && amount > 0 && (
                            <span className="tw-text-primary/70 tw-font-medium tw-mt-0.5">
                                {currencyFormatter(amount, currency)}
                            </span>
                        )}
                    </div>
                );
            })}
            <div className="tw-border-t tw-border-border tw-mx-1 tw-my-0.5" />

            <div className="tw-flex tw-flex-col tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-bg-foreground tw-text-background tw-text-xs">
                <span className="tw-uppercase tw-font-bold tw-tracking-wider tw-opacity-60">
                    {t("totals")}
                </span>
                <span className="tw-font-semibold tw-mt-1">
                    {renderDurationAsFormat(totalDuration, "HH:mm")}
                </span>
                {canViewRates && totalPayment > 0 && (
                    <span className="tw-opacity-70 tw-font-medium tw-mt-0.5">
                        {currencyFormatter(totalPayment, currency)}
                    </span>
                )}
            </div>

            {hasBudget && (
                <>
                    <div className="tw-border-t tw-border-border tw-mx-1 tw-my-0.5" />

                    <div className="tw-grid tw-grid-cols-2 tw-gap-1.5 tw-flex-wrap tw-text-[11px]">
                        <div className="tw-flex tw-flex-col tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-bg-white tw-border-border">
                            <span className="tw-uppercase tw-font-bold tw-tracking-wider tw-text-muted-foreground">
                                {t("budget-hours")}
                            </span>
                            <span className="tw-font-semibold tw-mt-1 tw-text-foreground">
                                {renderDurationAsFormat(budget.hours, "HH:mm")}
                            </span>
                        </div>

                        <div className={cn("tw-flex tw-flex-col tw-px-3 tw-py-2 tw-rounded-lg tw-border",
                            durationVar.isNegative ? "tw-bg-destructive/10 tw-border-destructive/20" : "tw-bg-primary/5 tw-border-primary/20"
                        )}>
                            <span className={cn("tw-uppercase tw-font-bold tw-tracking-wider",
                                durationVar.isNegative ? "tw-text-destructive" : "tw-text-primary"
                            )}>
                                {t("hours-var")}
                            </span>
                            <span className={cn("tw-font-semibold tw-mt-1",
                                durationVar.isNegative ? "tw-text-destructive" : "tw-text-foreground"
                            )}>
                                {durationVar.isNegative ? "-" : ""}{renderDurationAsFormat(durationVar.value, "HH:mm")}
                            </span>
                        </div>

                        {canViewRates && (
                            <>
                                <div className="tw-flex tw-flex-col tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-bg-white tw-border-border">
                                    <span className="tw-uppercase tw-font-bold tw-tracking-wider tw-text-muted-foreground">
                                        {t("budget-amount")}
                                    </span>
                                    <span className="tw-font-semibold tw-mt-1 tw-text-foreground">
                                        {currencyFormatter(budget.amount, currency)}
                                    </span>
                                </div>

                                <div
                                    className={cn("tw-flex tw-flex-col tw-px-3 tw-py-2 tw-rounded-lg tw-border",
                                        amountVar.isNegative ? "tw-bg-destructive/10 tw-border-destructive/20" : "tw-bg-primary/5 tw-border-primary/20"
                                    )}
                                >
                                    <span
                                        className={cn("tw-uppercase tw-font-bold tw-tracking-wider",
                                            amountVar.isNegative ? "tw-text-destructive" : "tw-text-primary"
                                        )}
                                    >
                                        {t("amount-var")}
                                    </span>
                                    <span
                                        className={cn("tw-font-semibold tw-mt-1",
                                            amountVar.isNegative ? "tw-text-destructive" : "tw-text-foreground"
                                        )}
                                    >
                                        {amountVar.isNegative ? "-" : ""}{currencyFormatter(amountVar.value, currency)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default Widget;
